/**
 * WebSocket клиент для работы с уведомлениями Battle Pairs
 *
 * Использует STOMP поверх нативного WebSocket.
 * Токен авторизации передаётся в query-параметре ?token=...
 * и берётся СВЕЖИМ при каждой попытке подключения (в т.ч. при реконнекте),
 * чтобы не ловить Invalid JWT после истечения срока жизни access-токена.
 *
 * @module battlePairsWebSocket
 */

import { Client } from '@stomp/stompjs';
import { getAccessToken } from './token';

/**
 * Базовый URL API без суффикса /api (для WebSocket)
 */
const getWsBaseUrl = () => {
  const apiUrl = process.env.REACT_APP_API_BASE_URL || 'https://api.hrnb.ru/api';
  const baseUrl = apiUrl.endsWith('/api') ? apiUrl.replace(/\/api$/, '') : apiUrl;
  return baseUrl.replace(/^http(s)?:\/\//, 'ws$1://');
};

/**
 * Построить URL WebSocket со свежим токеном в query-параметре
 * @returns {string} URL вида wss://api.hrnb.ru/ws?token=...
 */
const buildWsUrl = () => {
  const token = getAccessToken();
  if (!token) {
    console.error('[BattlePairsWS] JWT токен не найден');
    return `${getWsBaseUrl()}/ws`;
  }
  return `${getWsBaseUrl()}/ws?token=${encodeURIComponent(token)}`;
};

/**
 * Типы событий Battle Pairs
 * @enum {string}
 */
export const PairEventType = {
  PAIR_CREATED: 'PAIR_CREATED',
  PAIR_UPDATED: 'PAIR_UPDATED',
  PAIR_DELETED: 'PAIR_DELETED',
  PAIR_ACTIVATED: 'PAIR_ACTIVATED',
  PAIR_FINISHED: 'PAIR_FINISHED',
  PAIR_DISQUALIFIED: 'PAIR_DISQUALIFIED',
  WINNER_ANNOUNCED: 'WINNER_ANNOUNCED',
  DRAW_COMPLETED: 'DRAW_COMPLETED',
  TRACK_UPLOADED: 'TRACK_UPLOADED'
};

/**
 * Класс для управления WebSocket подключением к Battle Pairs
 */
class BattlePairsWebSocketClient {
  constructor(wsUrl) {
    this.wsUrl = wsUrl || getWsBaseUrl() + '/ws';
    this.client = null;
    this.subscriptions = new Map(); // Map<roundId, subscription>
    this.callbacks = new Map(); // Map<roundId, callback>
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 3000;
  }

  /**
   * Подключиться к WebSocket серверу
   * @returns {Promise<void>}
   */
  async connect() {
    if (this.client && this.client.connected) {
      console.log('[BattlePairsWS] Already connected');
      return;
    }

    this.client = new Client({
      // webSocketFactory вызывается при КАЖДОЙ попытке подключения,
      // поэтому токен в URL всегда свежий (важно при реконнектах)
      webSocketFactory: () => {
        const url = buildWsUrl();
        console.log(
          '[BattlePairsWS] Opening WebSocket:',
          url.replace(/token=[^&]+/, 'token=***')
        );
        return new WebSocket(url);
      },
      // Переподключаемся не более maxReconnectAttempts раз;
      // возврат -1 останавливает встроенный цикл переподключения stompjs
      reconnectDelay: (attempt) => {
        if (attempt > this.maxReconnectAttempts) {
          console.error(
            `[BattlePairsWS] WebSocket сервер недоступен (${this.wsUrl}). ` +
            'Переподключение остановлено — обновите страницу, чтобы повторить попытку.'
          );
          return -1;
        }
        return this.reconnectDelay;
      },
      debug: (str) => {
        console.log('[BattlePairsWS Debug]', str);
      },
      onConnect: () => {
        console.log('[BattlePairsWS] Connected');
        this.reconnectAttempts = 0;
        // Восстанавливаем подписки после переподключения
        this.restoreSubscriptions();
      },
      onStompError: (frame) => {
        console.error('[BattlePairsWS] STOMP error', frame);
      },
      onWebSocketError: (event) => {
        this.reconnectAttempts++;
        console.error(
          `[BattlePairsWS] WebSocket error (попытка ${this.reconnectAttempts}/${this.maxReconnectAttempts})`,
          event
        );
      },
      onDisconnect: () => {
        console.log('[BattlePairsWS] Disconnected');
      }
    });

    await this.client.activate();
  }

  /**
   * Отключиться от WebSocket сервера
   */
  disconnect() {
    if (this.client) {
      // Отписываемся от всех каналов
      this.unsubscribeAll();
      this.client.deactivate();
      this.client = null;
      console.log('[BattlePairsWS] Disconnected by user');
    }
  }

  /**
   * Подписаться на уведомления раунда
   * @param {string} roundId - UUID раунда
   * @param {Function} callback - Функция обратного вызова для событий
   * @returns {Function} Функция для отписки
   */
  subscribeToRound(roundId, callback) {
    if (!this.client || !this.client.connected) {
      console.error('[BattlePairsWS] Not connected');
      return () => {};
    }

    // Если уже подписаны, обновляем callback
    if (this.callbacks.has(roundId)) {
      this.callbacks.set(roundId, callback);
      console.log('[BattlePairsWS] Updated callback for round', roundId);
      return () => this.unsubscribeFromRound(roundId);
    }

    const topicDestination = `/topic/battle_pairs.round.${roundId}`;
    
    console.log('[BattlePairsWS] Subscribing to', topicDestination);
    
    const subscription = this.client.subscribe(topicDestination, (message) => {
      try {
        const event = JSON.parse(message.body);
        console.log('[BattlePairsWS] Received event:', event);
        
        // Вызываем callback для этого раунда
        const cb = this.callbacks.get(roundId);
        if (cb) {
          cb(event);
        }
      } catch (error) {
        console.error('[BattlePairsWS] Error parsing message:', error);
      }
    });

    // Сохраняем подписку и callback
    this.subscriptions.set(roundId, subscription);
    this.callbacks.set(roundId, callback);

    // Отправляем запрос на подписку (для трекинга на сервере)
    // В @stomp/stompjs v7 вместо send() используется publish()
    this.client.publish({
      destination: `/app/battle_pairs.join.round.${roundId}`,
      body: JSON.stringify({})
    });

    return () => this.unsubscribeFromRound(roundId);
  }

  /**
   * Отписаться от уведомлений раунда
   * @param {string} roundId - UUID раунда
   */
  unsubscribeFromRound(roundId) {
    const subscription = this.subscriptions.get(roundId);
    if (subscription) {
      try {
        subscription.unsubscribe();
      } catch (e) {
        console.error('[BattlePairsWS] Error unsubscribing:', e);
      }
      this.subscriptions.delete(roundId);
      this.callbacks.delete(roundId);

      // Отправляем запрос на отписку
      // В @stomp/stompjs v7 вместо send() используется publish()
      if (this.client && this.client.connected) {
        try {
          this.client.publish({
            destination: `/app/battle_pairs.leave.round.${roundId}`,
            body: JSON.stringify({})
          });
        } catch (e) {
          console.error('[BattlePairsWS] Error sending leave message:', e);
        }
      }

      console.log('[BattlePairsWS] Unsubscribed from round', roundId);
    }
  }

  /**
   * Отписаться от всех раундов
   */
  unsubscribeAll() {
    const roundIds = Array.from(this.subscriptions.keys());
    roundIds.forEach((roundId) => {
      this.unsubscribeFromRound(roundId);
    });
  }

  /**
   * Восстановить подписки после переподключения
   */
  restoreSubscriptions() {
    const callbacksToRestore = Array.from(this.callbacks.entries());
    this.callbacks.clear();
    this.subscriptions.clear();
    
    callbacksToRestore.forEach(([roundId, callback]) => {
      this.subscribeToRound(roundId, callback);
    });
  }

  /**
   * Проверка статуса подключения
   * @returns {boolean}
   */
  isConnected() {
    return this.client && this.client.connected;
  }
}

// Singleton экземпляр
let battlePairsWSClient = null;

/**
 * Получить экземпляр WebSocket клиента
 * @param {string} [wsUrl] - URL WebSocket сервера
 * @returns {BattlePairsWebSocketClient}
 */
export const getBattlePairsWSClient = (wsUrl) => {
  if (!battlePairsWSClient) {
    battlePairsWSClient = new BattlePairsWebSocketClient(wsUrl);
  }
  return battlePairsWSClient;
};

/**
 * Хук для использования в React компонентах (для функциональных компонентов)
 * 
 * @param {string} roundId - UUID раунда для подписки
 * @param {Function} onEvent - Обработчик событий
 * @returns {Object} { connected, subscribe, unsubscribe, disconnect }
 * 
 * @example
 * // В компоненте:
 * useEffect(() => {
 *   const ws = getBattlePairsWSClient();
 *   ws.connect();
 *   const unsubscribe = ws.subscribeToRound(roundId, handleEvent);
 *   return () => { unsubscribe(); ws.disconnect(); };
 * }, [roundId]);
 */
export const useBattlePairsWebSocket = (roundId, onEvent) => {
  // Это заготовка для будущего React хука
  // Реализация потребует использования useContext или создания кастомного хука
  console.warn(
    'useBattlePairsWebSocket: используйте getBattlePairsWSClient() напрямую в useEffect'
  );
  return {
    connected: battlePairsWSClient?.isConnected() ?? false,
    subscribe: () => battlePairsWSClient?.subscribeToRound(roundId, onEvent),
    unsubscribe: () => battlePairsWSClient?.unsubscribeFromRound(roundId),
    disconnect: () => battlePairsWSClient?.disconnect()
  };
};

export default {
  PairEventType,
  BattlePairsWebSocketClient,
  getBattlePairsWSClient,
  useBattlePairsWebSocket
};
